import React, { useState, useEffect, useMemo } from "react";
import {
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  message,
  Spin,
  Card,
  Table,
  Empty,
  Tooltip,
} from "antd";
import {
  SendOutlined,
  ReloadOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../../css/sm-art-common.css";
import CriticalOperationService from "../../services/CriticalOperationService";

const { Title, Text } = Typography;
const { Option } = Select;

const GarmentProductionReport = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [operationType, setOperationType] = useState("");
  const [loading, setLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);

  const [siteTitle, setSiteTitle] = useState("Loading...");
  const [deviceList, setDeviceList] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [operationHeaders, setOperationHeaders] = useState([]);
  const [reportData, setReportData] = useState(null);

  const dropdownSpinner = (
    <div className="dark-dropdown-spinner">
      <Spin size="small" />
    </div>
  );

  useEffect(() => {
    (async () => {
      const titleRes = await CriticalOperationService.getSiteTitle();
      if (titleRes.ok) {
        const title =
          titleRes.data?.title ||
          titleRes.data?.data?.title ||
          titleRes.data?.data?.site_title ||
          "Site Title";
        setSiteTitle(title);
      } else {
        setSiteTitle("Site Title");
      }

      try {
        setDeviceLoading(true);
        const devRes = await CriticalOperationService.getDevices();
        if (devRes.ok) {
          const devices = devRes.data?.data?.devices || [];
          setDeviceList(Array.isArray(devices) ? devices : []);
        } else {
          setDeviceList([]);
          message.error("Failed to load devices");
        }
      } catch (e) {
        console.error(e);
        setDeviceList([]);
        message.error("Device API error");
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, []);

  const formatDateRange = () => {
    if (!fromDate || !toDate) return "";
    const from = fromDate.format("YYYY-MM-DD");
    const to = toDate.format("YYYY-MM-DD");
    return from === to
      ? `[ ${from} ] No.of Garments Produced`
      : `[ ${from} ] - [ ${to} ] No.of Garments Produced`;
  };

  const getOutputPcsByLine = (lineNo) => {
    const counts = reportData?.data?.device_statistics?.device_counts;
    if (!counts) return 0;

    const yusData = counts["YUS002"];
    if (yusData && yusData.line_no === lineNo) {
      return yusData.count || 0;
    }
    return 0;
  };

  const loadData = async () => {
    if (!fromDate || !toDate) {
      message.warning("Please select From and To Date!");
      return;
    }
    if (fromDate.isAfter(toDate, "day")) {
      message.warning("From Date cannot be after To Date");
      return;
    }

    setLoading(true);

    const res = await CriticalOperationService.getOperationsWithMaster(
      fromDate.format("YYYY-MM-DD"),
      toDate.format("YYYY-MM-DD"),
      operationType || ""
    );

    if (!res.ok) {
      setTableData([]);
      setOperationHeaders([]);
      setReportData(null);
      setLoading(false);
      return;
    }

    const api = res.data;
    setReportData(api);

    const counts = api?.data?.device_statistics?.device_counts;

    if (api?.status && counts) {
      const grouped = {};
      Object.keys(counts).forEach((key) => {
        const item = counts[key];
        const line = item?.line_no || "N/A";

        if (!grouped[line]) grouped[line] = [];
        grouped[line].push({
          device_id: key,
          count: item?.count ?? 0,
          op_name: item?.op_name || "N/A",
          line_no: line,
        });
      });

      const table = Object.keys(grouped)
        .filter((line) => line && line !== "N/A" && String(line).trim() !== "")
        .map((line) => ({
          key: String(line),
          line_no: line,
          ops: grouped[line],
        }));

      if (!table.length) {
        setTableData([]);
        setOperationHeaders([]);
        message.info("No data found for selected filters.");
      } else {
        const maxOps = Math.max(...table.map((row) => row.ops.length));
        setOperationHeaders(Array.from({ length: maxOps }, (_, i) => i + 1));
        setTableData(table);
      }
    } else {
      setTableData([]);
      setOperationHeaders([]);
      message.info("No data found for selected filters.");
    }

    setLoading(false);
  };

  const resetData = () => {
    setFromDate(null);
    setToDate(null);
    setOperationType("");
    setTableData([]);
    setOperationHeaders([]);
    setReportData(null);
  };

  const setToday = () => {
    const d = dayjs();
    setFromDate(d);
    setToDate(d);
  };

  const setLast7Days = () => {
    setFromDate(dayjs().subtract(6, "day"));
    setToDate(dayjs());
  };

  const setThisMonth = () => {
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs());
  };

  const columns = useMemo(() => {
    const base = [
      {
        title: "Line No",
        dataIndex: "line_no",
        key: "line_no",
        fixed: "left",
        width: 110,
        align: "center",
        render: (v) => (
          <span className="sm-pill sm-pill-info">{v}</span>
        ),
      },
    ];

    const opCols = operationHeaders.map((num, idx) => ({
      title: `Critical Operation ${num}`,
      key: `op_${num}`,
      width: 240,
      align: "center",
      render: (_, row) => {
        const op = row.ops?.[idx];
        if (!op) return <Text type="secondary">—</Text>;

        return (
          <div className="sm-op-box">
            <div className="sm-op-title">{op.op_name}</div>
            <div className="sm-op-tags">
              <Tag color="cyan" style={{ fontWeight: 500 }}>
                {op.device_id}
              </Tag>
              <Tag color="green" style={{ fontWeight: 500 }}>
                {op.count}
              </Tag>
            </div>
          </div>
        );
      },
    }));

    const outCol = [
      {
        title: "Output Pcs",
        key: "output_pcs",
        fixed: "right",
        width: 140,
        align: "center",
        render: (_, row) => (
          <span className="sm-pill sm-pill-info">
            {getOutputPcsByLine(row.line_no)}
          </span>
        ),
      },
    ];

    return [...base, ...opCols, ...outCol];
  }, [operationHeaders, reportData]);

  const hasData = tableData.length > 0 && operationHeaders.length > 0;

  return (
    <div className="sm-page">
      <style>{`
        .dark-dropdown-spinner {
          text-align: center;
          padding: 8px;
        }

        .dark-dropdown-spinner .ant-spin,
        .dark-dropdown-spinner .ant-spin-dot {
          color: #000 !important;
        }

        .dark-dropdown-spinner .ant-spin-dot-item {
          background-color: #000 !important;
        }

        .sm-select.ant-select .ant-select-arrow,
        .sm-select.ant-select .ant-select-suffix {
          color: #000 !important;
        }

        .sm-select.ant-select .ant-select-arrow .anticon-loading,
        .sm-select.ant-select .ant-select-arrow .anticon-spin,
        .sm-select.ant-select .ant-select-suffix .anticon-loading,
        .sm-select.ant-select .ant-select-suffix .anticon-spin,
        .sm-select.ant-select .ant-select-arrow svg,
        .sm-select.ant-select .ant-select-suffix svg {
          color: #000 !important;
          fill: #000 !important;
        }

        .sm-page .sm-select.ant-select .ant-select-arrow *,
        .sm-page .sm-select.ant-select .ant-select-suffix * {
          color: #000 !important;
          fill: #000 !important;
        }
      `}</style>

      <div className="sm-page-inner">
        <div className="sm-header">
          <div className="sm-title-wrap">
            <Title level={3} className="sm-page-title">
              <ThunderboltOutlined style={{ color: "#2150FD" }} />
              Garment Production Report
            </Title>
            <Text className="sm-page-subtitle">
              Device-wise critical operations grouped by line.
            </Text>
          </div>

          <div className="sm-top-actions">
            <Tooltip title="Today">
              <Button className="sm-btn-soft" icon={<CalendarOutlined />} onClick={setToday}>
                Today
              </Button>
            </Tooltip>
            <Button className="sm-btn-soft" onClick={setLast7Days}>
              Last 7 Days
            </Button>
            <Button className="sm-btn-soft" onClick={setThisMonth}>
              This Month
            </Button>
          </div>
        </div>

        <Card
          className="sm-card"
          title={<span>Filters</span>}
          extra={
            <div className="sm-filter-actions">
              <Button
                className="sm-btn-soft"
                icon={<ReloadOutlined />}
                onClick={resetData}
                disabled={loading}
              >
                Reset
              </Button>
              <Button
                className="sm-btn-primary"
                icon={<SendOutlined />}
                onClick={loadData}
                loading={loading}
              >
                Load
              </Button>
            </div>
          }
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <label className="sm-label">From Date</label>
              <DatePicker
                className="sm-date-picker"
                value={fromDate}
                onChange={setFromDate}
                format="YYYY-MM-DD"
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <label className="sm-label">To Date</label>
              <DatePicker
                className="sm-date-picker"
                value={toDate}
                onChange={setToDate}
                format="YYYY-MM-DD"
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <label className="sm-label">Device Type</label>
              <Select
                className="sm-select"
                value={operationType || undefined}
                onChange={setOperationType}
                placeholder={deviceLoading ? "Loading devices..." : "Select Operation"}
                allowClear
                showSearch
                loading={deviceLoading}
                notFoundContent={deviceLoading ? dropdownSpinner : "No data"}
                optionFilterProp="children"
              >
                {deviceList.map((device, idx) => (
                  <Option key={idx} value={device}>
                    {device}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Optional header/range block */}
        {/* <div className="sm-report-center">
          <Title level={4} style={{ marginBottom: 4 }}>
            {siteTitle}
          </Title>
          {fromDate && toDate ? (
            <span className="sm-chip sm-chip-blue">{formatDateRange()}</span>
          ) : (
            <span className="sm-chip sm-chip-blue">Select date range and click Load</span>
          )}
        </div> */}

        <Card className="sm-card" style={{ marginTop: 16 }}>
          <Spin spinning={loading}>
            {!hasData ? (
              <div className="sm-empty-wrap">
                <Empty
                  description={
                    <span className="sm-empty-text">
                      No data found. Choose filters and click <b>Load</b>.
                    </span>
                  }
                />
              </div>
            ) : (
              <div className="sm-table-wrap">
                <Table
                  className="sm-table"
                  dataSource={tableData}
                  columns={columns}
                  bordered
                  size="middle"
                  pagination={false}
                  sticky
                  rowKey="key"
                  scroll={{ x: 110 + operationHeaders.length * 240 + 140, y: 520 }}
                />
              </div>
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default GarmentProductionReport;